/**
 * The page box for a receipt: NO page margin. The receipt's own margins
 * (top 3mm, right 2mm, bottom 3mm, left 1mm) are padding on the 53mm card
 * (index.css).
 *
 * No `size`: the only form that fits a roll, `58mm auto`, is not valid CSS
 * (`auto` may not follow a length), so the browser drops it; a single length
 * makes a 58mm SQUARE page and splits a long receipt. The paper is therefore
 * the printer driver's — set it to the 58mm roll there (or pin it with
 * Chrome's PrintingPaperSizeDefault policy).
 *
 * Why it is injected here rather than living in index.css: a named
 * `@page receipt-thermal` only takes effect through the `page` property, and
 * `page` is ignored on the absolutely-positioned #receipt-print — so the print
 * fell back to Chrome's default page, with its ~1cm margins and, inside them,
 * the date / title / URL / page-number header and footer. An unnamed `@page`
 * applies to every page, so it is present ONLY while a receipt print is in
 * flight and never reaches other window.print() callers (the shift report).
 * A zero margin also leaves Chrome no room to draw the header and footer.
 */
export const RECEIPT_PAGE_CSS = '@page { margin: 0; }';
const STYLE_ID = 'receipt-print-page';

/**
 * Fire the browser's print for the receipt only. Scopes the print to the
 * `#receipt-print` element via the `.printing-receipt` class on <html> (see the
 * thermal print rules in index.css) plus a temporary receipt @page rule, then
 * clears both on `afterprint` with a timeout fallback.
 *
 * Prints SILENTLY (no dialog) when Chrome runs with --kiosk-printing and the
 * thermal printer is the default; otherwise the normal print dialog opens.
 */
export function printReceipt(): void {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  root.classList.add('printing-receipt');

  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = RECEIPT_PAGE_CSS;
    document.head.appendChild(style);
  }

  const cleanup = () => {
    root.classList.remove('printing-receipt');
    document.getElementById(STYLE_ID)?.remove();
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
  setTimeout(cleanup, 1500);
}
